import { cn } from '@heroui/react';
import moment from 'moment';
import { Fragment, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import i18n from '~/common/helpers/i18n';
import { buildPipelineLogsUrl } from '~/common/helpers/url/monitorPaths';
import {
  DEPLOYMENT_ISSUE_ICON,
  DeploymentIssueInput,
  DeploymentIssueKind,
  getDeploymentIssues
} from '~/components/DeploymentIssueIndicator';
import Role from '~/components/Role';
import { Deployment } from '~/entities/Deployment';

type DeploymentAlertsSectionProps = {
  deployment: Deployment;
  onRedeploy: () => void;
};

/** The timestamp the Out of Memory title carries — `lastError` first, exactly as before. */
const getMessage = (deployment: Deployment) => {
  if (deployment?.lastError || deployment?.lastOMM) {
    return ` - ${moment(
      deployment?.lastError || deployment?.lastOMM,
      'x'
    ).format('DD/MM/YYYY HH:mm:ss')}`;
  }
  return '';
};

/**
 * A link closes the alert's text as one more underlined line of it, in the plain foreground colour
 * rather than an accent — the design draws no blue anywhere in these boxes.
 */
const ALERT_LINK_CLASS = 'w-fit text-label-lg text-on-surface underline';

/**
 * And a button is outlined, never filled: the tinted box is already the emphasis.
 *
 * `on-surface` and not `on-secondary-container`, which is what the design names the outline and the
 * label: that token is #6d0ff0 here and #0d0d0d there, and the design draws the button in the same
 * near-black as the description beside it. `on-surface` is that colour, in both themes.
 */
const ALERT_BUTTON_CLASS =
  'inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-solid border-on-surface px-2.5 text-label-lg text-on-surface';

/*
 * Both are plain elements rather than HeroUI's `Link` and `Button` bent into this shape: those merge
 * incoming classes with tailwind-merge, which reads `text-label-lg` as a colour class and drops it
 * in favour of the colour beside it, so the type token never arrives.
 */

type DocLinkProps = {
  href: string;
  children: ReactNode;
  'data-testid'?: string;
};

const DocLink = ({ href, children, ...props }: DocLinkProps) => (
  <a
    className={ALERT_LINK_CLASS}
    href={href}
    rel='noreferrer noopener'
    target='_blank'
    {...props}
  >
    {children}
  </a>
);

type IssueAlertProps = {
  color: 'warning' | 'danger';
  title?: string | null;
  description?: string | null;
  /** Closes the text as its last line, 4px under the description. */
  link?: ReactNode;
  /** Buttons, on their own row 12px under the text and 12px apart. */
  actions?: ReactNode;
};

/**
 * The tinted box and the colour its icon and title share, per severity. The title carries its type
 * token in the same string: `cn` would run the pair through tailwind-merge, which takes
 * `text-label-lg` for a colour class and would drop it in favour of the one beside it.
 */
const SEVERITY_STYLE: Record<
  IssueAlertProps['color'],
  { container: string; icon: string; title: string }
> = {
  danger: {
    container: 'bg-negative-container',
    icon: 'text-on-negative-container',
    title: 'text-label-lg text-on-negative-container'
  },
  warning: {
    container: 'bg-warning-container',
    icon: 'text-on-warning-container',
    title: 'text-label-lg text-on-warning-container'
  }
};

/**
 * One problem alert, as the design draws it: a tinted box, the severity icon beside a column of
 * text, and the title in the severity colour over a body in the plain foreground. A link runs on as
 * the text's last line; buttons get a row of their own under it.
 *
 * Hand-built rather than HeroUI's `Alert`, which is a different component — a 36px filled icon
 * circle, its own paddings and a border — and got the design's box only by overriding every slot
 * it has. The icon comes from `DEPLOYMENT_ISSUE_ICON`, the same map the listing indicator draws
 * from, so a problem is not a triangle on the card and something else here.
 */
const IssueAlert = ({
  color,
  title,
  description,
  link,
  actions
}: IssueAlertProps) => {
  const Icon = DEPLOYMENT_ISSUE_ICON[color];
  const severity = SEVERITY_STYLE[color];

  return (
    <div
      role='alert'
      className={cn(
        'flex gap-2.5 rounded-md px-3 pb-3.5 pt-3',
        severity.container
      )}
    >
      <Icon className={cn('icon-small shrink-0', severity.icon)} aria-hidden />
      <div className='flex min-w-0 flex-1 flex-col gap-3'>
        {/* A long word breaks rather than widening the box — an endpoint or a global's name has
            nowhere to wrap. */}
        <div className='flex flex-col gap-1 break-words'>
          <p className={severity.title}>{title}</p>
          <p className='text-title-sm text-on-surface'>{description}</p>
          {link}
        </div>
        {actions && <div className='flex gap-3'>{actions}</div>}
      </div>
    </div>
  );
};

type RedeployActionProps = {
  onRedeploy: () => void;
  /** Which alert this action belongs to — several are on screen at once. */
  'data-testid': string;
};

/** Redeploy is the documented fix for every warning here, and it stays permission-gated. */
const RedeployAction = ({
  onRedeploy,
  'data-testid': dataTestId
}: RedeployActionProps) => (
  <Role name='BETA:CREATE-DEPLOYMENT'>
    <button
      type='button'
      className={ALERT_BUTTON_CLASS}
      onClick={onRedeploy}
      data-testid={dataTestId}
    >
      {i18n.t('action.redeploy_pipeline')}
    </button>
  </Role>
);

/**
 * Every problem alert the beehive sidesheet raised, with the same copy, drawn as the design has
 * them.
 *
 * Which alerts show *and in which order* is decided by `getDeploymentIssues` — the same call the
 * listing indicator's tooltip is built from. Rendering them as a fixed run of JSX put the error
 * alert last while the tooltip led with it, so opening a card reshuffled the problems the user had
 * just read. The listing is what tells the user to open this drawer, so it cannot promise a problem
 * the drawer then denies, nor deal them out in a different order.
 *
 * The reverse is allowed, and one problem uses it: the drawer raises the error alert, which the
 * listing leaves out entirely (`LISTING_ISSUE_KINDS`). Nothing is promised there and denied here.
 *
 * One deliberate difference from beehive: the alerts were `collapsible`, so the explanation and
 * the redeploy button were hidden behind the title. Here they are open — the whole point of the
 * section is the mitigation, and hiding it behind a click was what let a degraded deployment look
 * like it had nothing wrong with it.
 */
export const DeploymentAlertsSection = ({
  deployment,
  onRedeploy
}: DeploymentAlertsSectionProps) => {
  const { realm, environment } = useParams();

  const issues = getDeploymentIssues(deployment as DeploymentIssueInput);

  // Read by the legacy sidesheet and kept for parity, although no deployment query asks for it.
  const needsProjectRedeploy = Boolean(deployment?.pipelineUpdateProject);

  if (issues.length === 0 && !needsProjectRedeploy) {
    return null;
  }

  const ALERT: Record<DeploymentIssueKind, ReactNode> = {
    errorsAndIssues: (
      <IssueAlert
        color='danger'
        title={i18n.t('label.error')}
        description={`${
          i18n.t(
            'label.pipeline_has_errors_issues_generic_mixed_warning_error_msg'
          ) || ''
        } ${i18n.t('label.error-message-pipeline-runtime') || ''}`}
        link={
          <Link
            className={ALERT_LINK_CLASS}
            target='_blank'
            data-testid='monitor-completed-executions-button-executions-details-see-all-logs'
            to={
              deployment?.pipeline?.name
                ? buildPipelineLogsUrl(
                    realm ?? '',
                    environment ?? '',
                    deployment.pipeline.name,
                    '1 Hour'
                  )
                : '#'
            }
          >
            {i18n.t('scenes.execution_finished.actions.see_all_logs')}
          </Link>
        }
      />
    ),
    outOfMemory: (
      <IssueAlert
        color='warning'
        title={`Out of Memory ${getMessage(deployment)}`}
        description={i18n.t('scenes.runtime.messages.error.out_of_memory')}
      />
    ),
    outdatedGlobal: (
      <IssueAlert
        color='warning'
        title={i18n.t('label.global_has_been_edited_details_title_msg')}
        description={i18n.t(
          'label.update_globals_redeploy_needed_card_details_msg_warning'
        )}
        link={
          <DocLink
            href={i18n.t('action.learn_more_about_globals_url') || ''}
            data-testid='run-detail-link-globals'
          >
            {i18n.t('action.learn_more_about_globals_warning_link')}
          </DocLink>
        }
        actions={
          <RedeployAction
            onRedeploy={onRedeploy}
            data-testid='run-detail-redeploy-globals'
          />
        }
      />
    ),
    outdatedAccount: (
      <IssueAlert
        color='warning'
        title={i18n.t('label.edited_account')}
        description={i18n.t(
          'label.update_accounts_redeploy_needed_card_details_msg_warning'
        )}
        link={
          <DocLink
            href={i18n.t('action.learn_more_about_accounts_warning_url') || ''}
            data-testid='run-detail-link-accounts'
          >
            {i18n.t('action.learn_more_about_accounts_warning_link')}
          </DocLink>
        }
        actions={
          <RedeployAction
            onRedeploy={onRedeploy}
            data-testid='run-detail-redeploy-accounts'
          />
        }
      />
    ),
    statusAlert: (
      <IssueAlert
        color='warning'
        title={i18n.t('common.labels.degraded')}
        description={i18n.t('scenes.runtime.messages.error.degraded')}
        link={
          <DocLink
            href={i18n.t('action.learn_more_about_deployment_status_url') || ''}
            data-testid='run-detail-link-deployment-status'
          >
            {i18n.t('action.learn_more_about_deployment_status_link')}
          </DocLink>
        }
        actions={
          <RedeployAction
            onRedeploy={onRedeploy}
            data-testid='run-detail-redeploy-degraded'
          />
        }
      />
    )
  };

  return (
    <section>
      <div className='flex flex-col gap-3'>
        {issues.map(issue => (
          <Fragment key={issue.kind}>{ALERT[issue.kind]}</Fragment>
        ))}

        {/* Last, and outside the ordered list: this one is not a problem
            `getDeploymentIssues` knows about. */}
        {needsProjectRedeploy && (
          <IssueAlert
            color='warning'
            title={i18n.t('label.needs_redeploy_alert')}
            description={i18n.t('label.needs_redeploy_msg')}
            actions={
              <RedeployAction
                onRedeploy={onRedeploy}
                data-testid='run-detail-redeploy-project'
              />
            }
          />
        )}
      </div>
    </section>
  );
};

export default DeploymentAlertsSection;
